'use client';

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { getFriendlyErrorMessage } from '@/utils/errorMessages';

const registerSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string()
    .min(6, "Password must be at least 6 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
})

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { signUp } = useAuth();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const validation = registerSchema.safeParse(formData);

    if (!validation.success) {
      setError(validation.error.issues[0].message);
      setLoading(false);
      return;
    }


    const { error: signUpError } = await signUp(formData.email, formData.password, formData.fullName);

    if (signUpError) {
      const { message } = getFriendlyErrorMessage(signUpError);
      setError(message);
      setLoading(false);
    } else {
      // Success - show modal
      setLoading(false);
      setShowSuccessModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    router.push('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link href="/" className="inline-block mb-4 relative w-16 h-16">
            <Image
              src="/images/logo.png"
              alt="Exotic and Domestic Logo"
              fill
              className="object-contain"
            />
          </Link>
          <h2 className="text-3xl font-headline font-bold text-foreground">
            Create an account
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Join ExoticAndDomestic to find your perfect companion
          </p>
        </div>

        <form className="mt-8 space-y-6 bg-card p-8 rounded-xl border border-border shadow-sm" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              required
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="John Doe"
            />

            <Input
              label="Email address"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="name@example.com"
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                helperText="At least 6 chars, 1 uppercase, 1 number"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[34px] text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            isLoading={loading}
          >
            Create Account
          </Button>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link href="/login" className="font-medium text-brand-primary hover:text-brand-accent">
              Sign in
            </Link>
          </div>
        </form>

        <Modal
          isOpen={showSuccessModal}
          onClose={handleCloseModal}
          title="Account Created Successfully"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Please check your email and confirm your email address to complete the registration process.
            </p>
            <div className="flex justify-end pt-4">
              <Button onClick={handleCloseModal}>
                Go to Login
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
